import type { IPlanReviewCommentApi } from '../api/types'
import { PLAN_REVIEW_COMMENTS_SECTION } from '../config/justification'
import {
  IPlanReviewJustificationParams,
  IPlanReviewJustificationRecord,
  PlanReviewSection,
  TPlanReviewJustificationRecords,
  TPlanReviewOffering,
} from '../model/types'

import { getPlanReviewQualityGroupKey } from './planReviewJustificationParams'

const PLAN_REVIEW_SECTIONS = new Set<string>(Object.values(PlanReviewSection))

const parseParams = (value: unknown): IPlanReviewJustificationParams | null => {
  try {
    const parsed = typeof value === 'string' ? (JSON.parse(value) as unknown) : value

    return parsed && typeof parsed === 'object' ? (parsed as IPlanReviewJustificationParams) : null
  } catch {
    return null
  }
}

const mapComment = (
  row: IPlanReviewCommentApi,
  {
    partnerId,
    fYear,
    programType,
    isGlobal,
  }: {
    partnerId: string
    fYear: number
    programType: TPlanReviewOffering
    isGlobal: boolean
  }
): IPlanReviewJustificationRecord | null => {
  const rowPartnerId = row.PartnerID ?? row.PartnerId

  if (
    Number(row.IsDel) === 1 ||
    !row.Comment?.trim() ||
    (row.Section && row.Section !== PLAN_REVIEW_COMMENTS_SECTION) ||
    (rowPartnerId && rowPartnerId !== partnerId)
  ) {
    return null
  }

  const params = parseParams(row.Params)
  const section = params?.PlanQualitySectionKey

  if (
    !params ||
    !section ||
    !PLAN_REVIEW_SECTIONS.has(section) ||
    params.PlanQualityGroupKey !== getPlanReviewQualityGroupKey(section) ||
    Number(params.FYear) !== fYear ||
    params.ProgramType !== programType ||
    params.IsGlobal !== isGlobal
  ) {
    return null
  }

  return {
    entryId: Number(row.EntryID ?? row.EntryId ?? 0),
    partnerId: rowPartnerId ?? partnerId,
    comment: row.Comment.trim(),
    params,
  }
}

export const mapPlanReviewCommentsFromApi = (
  rows: IPlanReviewCommentApi[],
  context: {
    partnerId: string
    fYear: number
    programType: TPlanReviewOffering
    isGlobal: boolean
  }
): TPlanReviewJustificationRecords =>
  rows.reduce<TPlanReviewJustificationRecords>((records, row) => {
    const comment = mapComment(row, context)

    if (!comment) {
      return records
    }

    const section = comment.params.PlanQualitySectionKey
    const current = records[section]

    if (!current || comment.entryId >= current.entryId) {
      records[section] = comment
    }

    return records
  }, {})
